import { TextField, TextFieldProps } from "@mui/material";
import { passwordManagerIgnoreInputProps } from "components/Forms/passwordManagerIgnoreInputProps";
import { Control, FieldValues, Path } from "react-hook-form";

import { SuggestedField } from "./SuggestedField";

type SuggestedTextFieldProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  needsReview: boolean;
} & Omit<TextFieldProps, "name" | "value" | "onChange" | "onBlur">;

/**
 * Convenience wrapper around SuggestedField for plain TextFields.
 * For custom inputs (checkboxes, numeric fields), use SuggestedField directly.
 */
export function SuggestedTextField<T extends FieldValues>({
  name,
  control,
  needsReview,
  ...textFieldProps
}: SuggestedTextFieldProps<T>) {
  return (
    <SuggestedField name={name} control={control} needsReview={needsReview}>
      {({ field, fieldState, isSuggested, sparkleAdornment }) => (
        <TextField
          {...field}
          {...textFieldProps}
          // Surface react-hook-form validation errors (e.g. from useValidatingForm)
          // on the field. A field error takes precedence over any static
          // helperText hint; an explicitly passed `error` still forces the state.
          error={!!fieldState.error || textFieldProps.error}
          helperText={fieldState.error?.message ?? textFieldProps.helperText}
          slotProps={{
            ...textFieldProps.slotProps,
            input: {
              ...textFieldProps.slotProps?.input,
              startAdornment: isSuggested ? sparkleAdornment : undefined,
            },
            htmlInput: {
              ...passwordManagerIgnoreInputProps,
              ...textFieldProps.slotProps?.htmlInput,
            },
          }}
        />
      )}
    </SuggestedField>
  );
}
